import { DocShape } from "@eave/shared/types/api";
import APIClient from "web_modules/APIClient";
import { docToUrl, addressObjToStr } from "./helpers";
import { MARK_FOR_DELETION } from "./values";

export interface DocumentFieldsUpdateRequest {
  document_fields: DocumentFieldUpdate[];
}

export interface DocumentFieldUpdate {
  section_key: string;
  section_index: number;
  field_key: string;
  field_value: string;
}

// Diff object creators
export const documentFieldUpdateDiff = (
  section_key: string,
  section_index: number,
  field_key: string,
  field_value: any,
  type?: any,
): DocumentFieldUpdate => {
  return {
    section_key,
    section_index,
    field_key,
    field_value: type === "address" ? addressObjToStr(field_value) : `${field_value}`,
  };
};

export const documentIsUsedUpdateDiff = (isUsed: boolean): DocumentFieldUpdate =>
  documentFieldUpdateDiff("used", 0, "not_used", !isUsed);

export const documentIsVisibleUpdateDiff = (isVisible: boolean): DocumentFieldUpdate =>
  documentFieldUpdateDiff("visibility", 0, "show_on_borrower_dashboard", isVisible);

// Creates a list of diff objects using the helpers above
export const createDocumentFieldsDiff = (
  doc: DocShape,
  updatedFieldsMap: any,
  isUsed: boolean,
  isVisible: boolean,
): DocumentFieldUpdate[] => {
  const sectionKeys = Object.keys(updatedFieldsMap);
  const sectionsAsDiffs = sectionKeys
    .filter(key => key !== "used" && key !== "visibility")
    .filter(key => updatedFieldsMap[key] !== null && doc.sections.some(s => s.section_key === key))
    .map((sectionKey): DocumentFieldUpdate[] => {
      const sectionIndexes = Object.keys(updatedFieldsMap[sectionKey]);
      return sectionIndexes
        .map((sectionIndex): DocumentFieldUpdate[] => {
          const subKeys = Object.keys(updatedFieldsMap[sectionKey][sectionIndex]);
          return subKeys.map(
            (subKey: string): DocumentFieldUpdate => {
              // Find the section
              const docSubSection = doc.sections.find(s => s.section_key === sectionKey);

              // Grab that section's "fields"
              const docFields = docSubSection.fields.find(f => f.field_key === subKey);

              // Grab that field's type
              const type = docFields.field_type;
              return documentFieldUpdateDiff(
                sectionKey,
                Number(sectionIndex),
                subKey,
                updatedFieldsMap[sectionKey][sectionIndex][subKey],
                type,
              );
            },
          );
        })
        .reduce(
          (allSectionDiffs, currentSectionIndexDiffs) => [
            ...allSectionDiffs,
            ...currentSectionIndexDiffs,
          ],
          [],
        );
    })
    .reduce((allDiffs, currentSectionDiffs) => [...allDiffs, ...currentSectionDiffs], []); // flatten 1 layer

  return [
    documentIsUsedUpdateDiff(isUsed),
    documentIsVisibleUpdateDiff(isVisible),
    ...sectionsAsDiffs,
  ];
};

export const updateDocumentRequest = (doc: DocShape): Promise<any> => {
  return APIClient.put(`/documents/${doc.id}`, doc);
};

export const updateDocumentFieldsRequest = (
  docId: number,
  fieldsToUpdate: DocumentFieldUpdate[],
): Promise<any> => {
  const request: DocumentFieldsUpdateRequest = {
    document_fields: fieldsToUpdate.filter(
      (field: DocumentFieldUpdate) => field.field_value !== MARK_FOR_DELETION,
    ),
  };
  return APIClient.put(`/documents/${docId}/document_fields`, request);
};

export const updateDocumentVisibility = (
  docID: number,
  documentFieldsDiff: DocumentFieldUpdate[],
) => {
  // This is nasty but because the doc fields of "not_used" and "show_on_borrower_dashboard" can be changed on the
  // document hurdle page which doesn't load the rest of the document fields because "not_used" and
  // "show_on_borrower_dashboard" are duplicated in the document payload. We can't use the above function
  // updateDocumentFieldsRequest because it performs a PUT operation and would therefore delete all the other document
  // fields. We can however use the /document_field_list update endpoint however this was meant for bulk marking
  // documents as used/not used and does not have the concept of section_key.
  //
  // Ideally we could just call the UpdateDocument endpoint instead as the Document payload is where we read "not_used"
  // and "show_on_borrower_dashboard" in the first place but this endpoint does not allow for that due to the underlying
  // database having these fields in the 'document_fields' table instead of arguably where they should be: the 'documents' table

  const visibilityFieldsToUpdate = (documentFieldsDiff || [])
    .filter(
      (field: DocumentFieldUpdate) =>
        (field.section_key === "used" && field.field_key === "not_used") ||
        (field.section_key === "visibility" && field.field_key === "show_on_borrower_dashboard"),
    )
    .map((field: DocumentFieldUpdate) => {
      return {
        document_id: docID,
        section: field.section_key,
        field_key: field.field_key,
        field_value: field.field_value,
      };
    });

  if (visibilityFieldsToUpdate.length > 0) {
    return APIClient.put(`/document_field_list`, {
      document_fields: visibilityFieldsToUpdate,
    });
  }

  return Promise.resolve();
};

// Helper that abstracts away basically all of the update logic and returns an array of Promises
export const saveDocumentUpdates = (
  doc: DocShape,
  documentFieldsDiff: DocumentFieldUpdate[],
  additionalPromises?: Promise<any>[],
): Promise<any> => {
  const promises = [
    updateDocumentRequest(doc),
    updateDocumentFieldsRequest(doc.id, documentFieldsDiff),
  ];

  if (additionalPromises) {
    promises.push(...additionalPromises);
  }

  return Promise.all(promises);
};

export const fetchDocData = (doc: DocShape): Promise<any> => {
  return fetch(docToUrl(doc), { credentials: "same-origin" })
    .then((resp: Response) => {
      if (!resp.ok) {
        throw new Error(`${resp.status}: ${resp.statusText}`);
      }

      return resp
        .blob()
        .then((blob: Blob) => URL.createObjectURL(blob))
        .catch(() => {
          throw new Error("Couldn't get document blob.");
        });
    })
    .catch(() => {
      throw new Error("Couldn't show document because of a network issue.");
    });
};
